import { InputMask, Replacement } from '@react-input/mask';
import classNames from 'classnames';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { createFormFieldStyles } from '~/constants/styles';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import { getFieldError } from '../formUtils';
import { FormFieldProps } from '../interfaces';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends FormFieldProps<TFieldValues, TName> {
  autoComplete?: string;
  mask: string;
  replacement: Replacement;
  showMask?: boolean;
}

export const MaskedInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    disabled,
    cyTag,
    label,
    mask,
    replacement,
    className,
    flagged,
    required,
    tooltip,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const { showMask = true } = props;
  const error = getFieldError(errors, name);
  return (
    <Controller
      name={name}
      render={({ field }) => {
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={`${name}-helper`}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <InputMask
                {...field}
                id={name}
                value={field.value || ''}
                showMask={showMask}
                className={classNames(createFormFieldStyles(flagged, disabled, !!error), className)}
                mask={mask}
                replacement={replacement}
                disabled={disabled}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-describedby={
                  error?.message ? `${name}-error` : description ? `${name}-helper` : undefined
                }
                aria-errormessage={error?.message ? `${name}-error` : undefined}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
